import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bookingBrandFontFamilyVar,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import {
  BOOKING_SLUG_PATTERN,
  decodeClubRouteKey,
  parseBookingPathClubSlug,
} from '../../utils/club-route-key';
import { bookPathFromClubRouteKey } from '../../utils/booking-nav-paths';
import {
  defaultShopPaymentMethodCode,
  filterShopCheckoutPaymentMethods,
  filterShopSecondaryPaymentMethods,
} from '../../utils/shop-checkout-payment-methods';
import {
  buildBookingSurfaceStyles,
  resolveBookingBrandColors,
} from '../../utils/booking-brand-colors';
import { brandSettingsService } from '../../services/brand-settings';
import { customerAuthService, type BookingCustomer } from '../../services/customer-auth.service';
import {
  completeShopOrder,
  createShopCheckoutIntent,
  fetchShopCatalog,
  quoteShopCart,
  validateShopGiftCard,
  type ShopCartLine,
  type ShopCatalogPass,
  type ShopCompleteResult,
  type ShopGiftCardLine,
  type ShopGiftCardValidation,
  type ShopQuote,
} from '../../services/shop';
import type { CheckoutIntentResponse } from '../../services/payments';
import { walletApi, type WalletSummary } from '../../services/wallet';
import LoginRegisterModal from '../OnlineBooking/LoginRegisterModal';
import BookingSubNav from '../BookingShell/BookingSubNav';
import BookingAccountMenu from '../BookingShell/BookingAccountMenu';
import GolferDashboard from '../GolferDashboard/GolferDashboard';
import { useIsMobile } from '../ui/use-mobile';
import { formatMoneyFromCents } from '../../utils/format-money';
import GiftCardIcon from '../Icons/GiftCardIcon';
import { PassesIcon } from '../Icons/PassesIcon';
import {
  ShopGiftCardsSection,
  ShopPassesSection,
  ShopMembershipsSection,
  shopCartPassIds,
  shopCardStyle,
} from './ShopCatalogCards';
import ShopCheckoutPanel from './ShopCheckoutPanel';
import ShopMembershipRequestModal from './ShopMembershipRequestModal';
import ShopGiftCardPurchaseModal from './ShopGiftCardPurchaseModal';
import type { ShopCatalogMembership } from '../../services/shop';
import { isShopGiftCardGiftLine } from '../../utils/shop-gift-card-line';

function useClubRouteKey(): string | null {
  const location = useLocation();
  return useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(location.search);
    const slugParam = params.get('bookingSlug')?.trim().toLowerCase();
    if (slugParam && BOOKING_SLUG_PATTERN.test(slugParam)) return `s:${slugParam}`;
    const clubId = params.get('clubId')?.trim();
    if (clubId) return /^\d+$/.test(clubId) ? `n:${clubId}` : `i:${clubId}`;
    const clubNumber = params.get('clubNumber')?.trim();
    if (clubNumber) return `n:${clubNumber}`;
    const pathSlug = parseBookingPathClubSlug(location.pathname);
    if (pathSlug) return `s:${pathSlug}`;
    if (import.meta.env.DEV || window.location.hostname === 'localhost') return 'n:1';
    return null;
  }, [location.pathname, location.search]);
}

const ShopPage: React.FC = () => {
  const tebox = useBookingTebox();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clubRouteKey = useClubRouteKey();

  const [authenticatedCustomer, setAuthenticatedCustomer] = useState<BookingCustomer | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [cart, setCart] = useState<ShopCartLine[]>([]);
  const [customGiftDollars, setCustomGiftDollars] = useState('');
  const [step, setStep] = useState<'browse' | 'confirmation'>('browse');
  const [quote, setQuote] = useState<ShopQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [checkoutIntent, setCheckoutIntent] = useState<CheckoutIntentResponse | null>(null);
  const [checkoutChargeCents, setCheckoutChargeCents] = useState<number | null>(null);
  const [pendingGiftCardSecondary, setPendingGiftCardSecondary] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [orderResult, setOrderResult] = useState<ShopCompleteResult | null>(null);
  const [selectedPaymentMethodCode, setSelectedPaymentMethodCode] = useState<string | undefined>();
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardValidation, setGiftCardValidation] = useState<ShopGiftCardValidation | null>(null);
  const [giftCardValidating, setGiftCardValidating] = useState(false);
  const [secondaryPaymentMethodCode, setSecondaryPaymentMethodCode] = useState<string | undefined>();
  const [walletSummary, setWalletSummary] = useState<WalletSummary | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [membershipRequestTarget, setMembershipRequestTarget] = useState<ShopCatalogMembership | null>(null);
  const [membershipRequestSuccess, setMembershipRequestSuccess] = useState<string | null>(null);
  const [giftCardModalAmountCents, setGiftCardModalAmountCents] = useState<number | null>(null);
  const [orderGiftLineDetails, setOrderGiftLineDetails] = useState<
    Array<{ amountCents: number; recipientEmail?: string; recipientName?: string; message?: string }>
  >([]);

  useLayoutEffect(() => {
    if (clubRouteKey) {
      const cached = brandSettingsService.getCachedBrandSettings(clubRouteKey);
      if (cached) brandSettingsService.applyBrandSettingsSync(cached);
    }
  }, [clubRouteKey]);

  const { data: brandSettingsData } = useQuery({
    queryKey: ['brandSettings', clubRouteKey],
    queryFn: async () => {
      if (!clubRouteKey) return null;
      return brandSettingsService.getByClubId(clubRouteKey);
    },
    enabled: !!clubRouteKey,
    initialData: clubRouteKey ? brandSettingsService.getCachedBrandSettings(clubRouteKey) ?? undefined : undefined,
  });

  useEffect(() => {
    if (brandSettingsData) {
      void brandSettingsService.applyBrandSettings(brandSettingsData);
    }
  }, [brandSettingsData]);

  useEffect(() => {
    if (!clubRouteKey) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void queryClient.invalidateQueries({ queryKey: ['brandSettings', clubRouteKey] });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [queryClient, clubRouteKey]);

  const brandColors = useMemo(
    () => resolveBookingBrandColors(brandSettingsData),
    [brandSettingsData],
  );

  const surface = useMemo(() => buildBookingSurfaceStyles(brandColors), [brandColors]);

  const cartPassIds = useMemo(() => shopCartPassIds(cart), [cart]);

  const { data: catalog, isLoading: catalogLoading, error: catalogError } = useQuery({
    queryKey: ['shopCatalog', clubRouteKey],
    queryFn: () => fetchShopCatalog(clubRouteKey),
    enabled: !!clubRouteKey,
  });

  const paymentMethods = useMemo(() => catalog?.paymentMethods ?? [], [catalog?.paymentMethods]);
  const cartHasGiftCardPurchase = useMemo(
    () => cart.some((line) => line.type === 'gift_card'),
    [cart],
  );

  const walletBackedPaymentMethodsAllowed = Boolean(
    authenticatedCustomer && !walletLoading && walletSummary?.ledger?.id,
  );

  const shopPaymentOptions = useMemo(
    () => ({
      cartHasGiftCardPurchase,
      walletBackedPaymentMethodsAllowed,
      paymentsReady: catalog?.paymentsReady ?? false,
      devPaymentBypassAvailable: catalog?.devPaymentBypassAvailable ?? false,
    }),
    [
      cartHasGiftCardPurchase,
      walletBackedPaymentMethodsAllowed,
      catalog?.paymentsReady,
      catalog?.devPaymentBypassAvailable,
    ],
  );

  const selectablePaymentMethods = useMemo(
    () => filterShopCheckoutPaymentMethods(paymentMethods, shopPaymentOptions),
    [paymentMethods, shopPaymentOptions],
  );

  const selectedPaymentMethod = useMemo(
    () =>
      selectablePaymentMethods.find((method) => method.code === selectedPaymentMethodCode) ??
      selectablePaymentMethods[0],
    [selectablePaymentMethods, selectedPaymentMethodCode],
  );

  const walletBalanceLabel = useMemo(() => {
    if (!walletSummary?.ledger?.id) return null;
    const ledger = walletSummary.ledger;
    if (ledger.mode === 'CREDIT') {
      const available = ledger.availableCredit ?? ledger.balance ?? 0;
      return `Available credit: ${formatMoneyFromCents(Math.round(available * 100))}`;
    }
    return `Balance: ${formatMoneyFromCents(Math.round((ledger.balance ?? 0) * 100))}`;
  }, [walletSummary]);

  useEffect(() => {
    void customerAuthService.getCurrentUser().then(setAuthenticatedCustomer);
  }, []);

  const handleAccountLogout = useCallback(async () => {
    await customerAuthService.logout();
    setAuthenticatedCustomer(null);
    setShowDashboard(false);
  }, []);

  useEffect(() => {
    if (!authenticatedCustomer?.id) {
      setWalletSummary(null);
      return;
    }
    let cancelled = false;
    setWalletLoading(true);
    void walletApi
      .getCustomerSummary(authenticatedCustomer.id)
      .then((summary) => {
        if (!cancelled) setWalletSummary(summary);
      })
      .catch(() => {
        if (!cancelled) setWalletSummary(null);
      })
      .finally(() => {
        if (!cancelled) setWalletLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authenticatedCustomer?.id]);

  useEffect(() => {
    if (!selectablePaymentMethods.length) return;
    const stillValid = selectablePaymentMethods.some(
      (method) => method.code === selectedPaymentMethodCode,
    );
    if (!stillValid) {
      setSelectedPaymentMethodCode(defaultShopPaymentMethodCode(selectablePaymentMethods));
    }
  }, [selectablePaymentMethods, selectedPaymentMethodCode]);

  useEffect(() => {
    setCheckoutIntent(null);
    setCheckoutChargeCents(null);
    setPendingGiftCardSecondary(false);
    setGiftCardValidation(null);
  }, [cart, selectedPaymentMethodCode]);

  useEffect(() => {
    setGiftCardValidation(null);
  }, [giftCardCode]);

  const addGiftCardToCart = useCallback(
    (line: ShopGiftCardLine) => {
      setCart((prev) => [...prev, line]);
    },
    [],
  );

  const openGiftCardModal = useCallback((amountCents: number) => {
    setGiftCardModalAmountCents(amountCents);
  }, []);

  const openCustomGiftCardModal = useCallback(() => {
    const dollars = parseFloat(customGiftDollars);
    if (!Number.isFinite(dollars) || dollars <= 0) return;
    setGiftCardModalAmountCents(Math.round(dollars * 100));
    setCustomGiftDollars('');
  }, [customGiftDollars]);

  const addPassToCart = useCallback((pass: ShopCatalogPass) => {
    setCart((prev) => {
      if (prev.some((l) => l.type === 'pass' && l.passProductId === pass.id)) return prev;
      return [...prev, { type: 'pass', passProductId: pass.id }];
    });
  }, []);

  const removeCartLine = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setCheckoutIntent(null);
    setCheckoutError(null);
    if (!cart.length) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    void quoteShopCart(cart, clubRouteKey)
      .then((nextQuote) => {
        if (!cancelled) setQuote(nextQuote);
      })
      .catch((err) => {
        if (!cancelled) {
          setCheckoutError(err instanceof Error ? err.message : 'Failed to update cart totals');
        }
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cart, clubRouteKey]);

  const finishOrder = useCallback(
    (result: ShopCompleteResult) => {
      setOrderGiftLineDetails(
        cart
          .filter((line): line is ShopGiftCardLine => line.type === 'gift_card')
          .map((line) => ({
            amountCents: line.amountCents,
            recipientEmail: line.recipientEmail,
            recipientName: line.recipientName,
            message: line.message,
          })),
      );
      setOrderResult(result);
      setCart([]);
      setStep('confirmation');
      setCheckoutIntent(null);
      setCheckoutChargeCents(null);
      setPendingGiftCardSecondary(false);
      setGiftCardCode('');
      setGiftCardValidation(null);
      void queryClient.invalidateQueries({ queryKey: ['customerGiftCards'] });
      void queryClient.invalidateQueries({ queryKey: ['customerPasses'] });
    },
    [queryClient, cart],
  );

  const continueToCardPayment = async (options?: {
    amountCents?: number;
    forGiftCardSecondary?: boolean;
  }) => {
    if (!cart.length || !authenticatedCustomer || !quote) return;
    const cardMethod = selectablePaymentMethods.find((method) => method.channel === 'card');
    if (!cardMethod) {
      setCheckoutError(
        catalog?.devPaymentBypassAvailable
          ? 'Card checkout is unavailable. Try again or contact the club.'
          : catalog?.paymentsReady === false
            ? 'Card payments are not set up for this club yet. Contact the club to finish online payment setup.'
            : 'Card payments are not enabled for this club.',
      );
      return;
    }
    const chargeCents = options?.amountCents ?? quote.totalCents;
    setBusy(true);
    setCheckoutError(null);
    try {
      const intent = await createShopCheckoutIntent({
        lines: cart,
        customerId: authenticatedCustomer.id,
        clubRouteKey,
        paymentMethodCode: cardMethod.code,
        amountCents: options?.amountCents,
      });
      setCheckoutChargeCents(chargeCents);
      setPendingGiftCardSecondary(Boolean(options?.forGiftCardSecondary));
      setCheckoutIntent(intent);
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Checkout failed');
    } finally {
      setBusy(false);
    }
  };

  const payWithWallet = async () => {
    if (!cart.length || !authenticatedCustomer || !quote) return;
    const walletMethod = selectablePaymentMethods.find((method) => method.channel === 'wallet');
    if (!walletMethod) {
      setCheckoutError('Wallet payments are not enabled for this club.');
      return;
    }
    setBusy(true);
    setCheckoutError(null);
    try {
      const result = await completeShopOrder({
        lines: cart,
        customerId: authenticatedCustomer.id,
        paymentMethodCode: walletMethod.code,
        clubRouteKey,
      });
      finishOrder(result);
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Could not complete order');
    } finally {
      setBusy(false);
    }
  };

  const validateGiftCard = async () => {
    if (!cart.length || !authenticatedCustomer || !giftCardCode.trim()) return;
    setGiftCardValidating(true);
    setCheckoutError(null);
    try {
      const validation = await validateShopGiftCard({
        lines: cart,
        customerId: authenticatedCustomer.id,
        giftCardCode: giftCardCode.trim(),
        clubRouteKey,
      });
      setGiftCardValidation(validation);
      if (validation.requiresSecondaryPayment) {
        const secondaryOptions = filterShopSecondaryPaymentMethods(
          paymentMethods,
          walletBackedPaymentMethodsAllowed,
          shopPaymentOptions,
        );
        if (
          !secondaryPaymentMethodCode ||
          !secondaryOptions.some((method) => method.code === secondaryPaymentMethodCode)
        ) {
          setSecondaryPaymentMethodCode(secondaryOptions[0]?.code);
        }
      } else {
        setSecondaryPaymentMethodCode(undefined);
      }
    } catch (err) {
      setGiftCardValidation(null);
      setCheckoutError(err instanceof Error ? err.message : 'Gift card could not be validated');
    } finally {
      setGiftCardValidating(false);
    }
  };

  const payWithGiftCard = async () => {
    if (!cart.length || !authenticatedCustomer || !selectedPaymentMethod) return;
    if (selectedPaymentMethod.channel !== 'gift_card') return;

    let validation = giftCardValidation;
    if (!validation?.valid) {
      if (!giftCardCode.trim()) {
        setCheckoutError('Enter a gift card code.');
        return;
      }
      setBusy(true);
      setCheckoutError(null);
      try {
        validation = await validateShopGiftCard({
          lines: cart,
          customerId: authenticatedCustomer.id,
          giftCardCode: giftCardCode.trim(),
          clubRouteKey,
        });
        setGiftCardValidation(validation);
        if (validation.requiresSecondaryPayment) {
          const secondaryOptions = filterShopSecondaryPaymentMethods(
            paymentMethods,
            walletBackedPaymentMethodsAllowed,
          );
          const nextSecondary =
            secondaryPaymentMethodCode &&
            secondaryOptions.some((method) => method.code === secondaryPaymentMethodCode)
              ? secondaryPaymentMethodCode
              : secondaryOptions[0]?.code;
          setSecondaryPaymentMethodCode(nextSecondary);
        }
      } catch (err) {
        setCheckoutError(err instanceof Error ? err.message : 'Gift card could not be validated');
        setBusy(false);
        return;
      }
      setBusy(false);
    }

    if (!validation?.valid) return;

    const resolvedSecondaryCode =
      secondaryPaymentMethodCode ??
      (validation.requiresSecondaryPayment
        ? filterShopSecondaryPaymentMethods(
            paymentMethods,
            walletBackedPaymentMethodsAllowed,
            shopPaymentOptions,
          )[0]
            ?.code
        : undefined);

    if (validation.requiresSecondaryPayment) {
      const secondaryMethod = paymentMethods.find(
        (method) => method.code === resolvedSecondaryCode,
      );
      if (!secondaryMethod) {
        setCheckoutError('Choose a secondary payment method for the remaining balance.');
        return;
      }
      if (secondaryMethod.channel === 'card') {
        await continueToCardPayment({
          amountCents: validation.remainderCents,
          forGiftCardSecondary: true,
        });
        return;
      }
      if (secondaryMethod.channel === 'wallet') {
        setBusy(true);
        setCheckoutError(null);
        try {
          const result = await completeShopOrder({
            lines: cart,
            customerId: authenticatedCustomer.id,
            paymentMethodCode: selectedPaymentMethod.code,
            giftCardCode: giftCardCode.trim(),
            giftCardId: validation.giftCardId,
            secondaryPaymentMethodCode: secondaryMethod.code,
            clubRouteKey,
          });
          finishOrder(result);
        } catch (err) {
          setCheckoutError(err instanceof Error ? err.message : 'Could not complete order');
        } finally {
          setBusy(false);
        }
        return;
      }
      setCheckoutError('Secondary payment method is not supported.');
      return;
    }

    setBusy(true);
    setCheckoutError(null);
    try {
      const result = await completeShopOrder({
        lines: cart,
        customerId: authenticatedCustomer.id,
        paymentMethodCode: selectedPaymentMethod.code,
        giftCardCode: giftCardCode.trim(),
        giftCardId: validation.giftCardId,
        clubRouteKey,
      });
      finishOrder(result);
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Could not complete order');
    } finally {
      setBusy(false);
    }
  };

  const onPaymentSuccess = async ({ paymentIntentId }: { paymentIntentId?: string }) => {
    if (!paymentIntentId || !authenticatedCustomer) return;
    setBusy(true);
    setCheckoutError(null);
    try {
      if (pendingGiftCardSecondary && giftCardValidation?.valid && selectedPaymentMethod) {
        const secondaryMethod = paymentMethods.find(
          (method) => method.code === secondaryPaymentMethodCode,
        );
        const result = await completeShopOrder({
          lines: cart,
          customerId: authenticatedCustomer.id,
          paymentMethodCode: selectedPaymentMethod.code,
          giftCardCode: giftCardCode.trim(),
          giftCardId: giftCardValidation.giftCardId,
          secondaryPaymentMethodCode: secondaryMethod?.code,
          secondaryPaymentIntentId: paymentIntentId,
          clubRouteKey,
        });
        finishOrder(result);
        return;
      }

      const cardMethod = paymentMethods.find((method) => method.channel === 'card');
      if (!cardMethod) {
        setCheckoutError('Card payments are not enabled for this club.');
        return;
      }
      const result = await completeShopOrder({
        lines: cart,
        customerId: authenticatedCustomer.id,
        paymentMethodCode: cardMethod.code,
        paymentIntentId,
        clubRouteKey,
      });
      finishOrder(result);
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Could not complete order');
    } finally {
      setBusy(false);
    }
  };

  if (!clubRouteKey) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p style={{ fontFamily: bookingBrandFontFamilyVar }}>Open this page with a valid club link.</p>
      </div>
    );
  }

  if (catalog && !catalog.shopEnabled) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="p-6 text-center">
          <p style={{ fontFamily: bookingBrandFontFamilyVar, color: brandColors.textColor }}>
            The shop is not available for this club.
          </p>
          <button
            type="button"
            className="mt-4 underline"
            style={{ fontFamily: bookingBrandFontFamilyVar, color: brandColors.textColor }}
            onClick={() => navigate(bookPathFromClubRouteKey(clubRouteKey))}
          >
            Back to booking
          </button>
        </div>
      </div>
    );
  }

  const clubName = catalog?.clubName || 'Golf Club';
  const giftConfig = catalog?.giftCardConfig;

  if (showDashboard && authenticatedCustomer) {
    const dashboardWelcomeShort =
      authenticatedCustomer.firstName?.split('.')[0]?.split(' ')[0] ||
      authenticatedCustomer.firstName?.trim() ||
      'Guest';

    return (
      <div className="golfer-dashboard-scroll-shell">
        {isMobile ? (
          <div
            className="sticky top-0 bg-white border-b border-tebox-border px-4 py-3 shrink-0 relative"
            style={{ zIndex: 110 }}
          >
            <div className="flex items-center justify-between gap-3 min-w-0">
              <h1
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontWeight: 500,
                  fontSize: 20,
                  lineHeight: '31.5px',
                  color: brandColors.textColor,
                  margin: 0,
                  minWidth: 0,
                  flex: '1 1 auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`Hello, ${dashboardWelcomeShort}`}
              </h1>
              <BookingAccountMenu
                authenticatedCustomer={authenticatedCustomer}
                brandColors={brandColors}
                isMobile={isMobile}
                onSignIn={() => setShowLoginModal(true)}
                onLogout={handleAccountLogout}
                showDashboard={showDashboard}
                onOpenDashboard={() => setShowDashboard(true)}
                onCloseDashboard={() => setShowDashboard(false)}
                dashboardBackLabel="Back to shop"
              />
            </div>
          </div>
        ) : null}
        <GolferDashboard
          onBackToBooking={() => setShowDashboard(false)}
          brandColors={brandColors}
          omitWelcomeHeading={isMobile}
        />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ ...surface.page, WebkitTapHighlightColor: 'transparent' }}
    >
      <div className={`bg-white ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} relative`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brandSettingsData?.logoUrl ? (
              <img
                src={brandSettingsData.logoUrl}
                alt={clubName}
                style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, objectFit: 'contain' }}
              />
            ) : null}
            <h1
              style={{
                ...surface.heading,
                fontSize: isMobile ? '20px' : '24px',
              }}
            >
              {clubName}
            </h1>
          </div>
          <BookingAccountMenu
            authenticatedCustomer={authenticatedCustomer}
            brandColors={brandColors}
            isMobile={isMobile}
            onSignIn={() => setShowLoginModal(true)}
            onLogout={handleAccountLogout}
            showDashboard={showDashboard}
            onOpenDashboard={() => setShowDashboard(true)}
            onCloseDashboard={() => setShowDashboard(false)}
            dashboardBackLabel="Back to shop"
          />
        </div>
      </div>

      <BookingSubNav
        active="shop"
        clubRouteKey={clubRouteKey}
        textColor={brandColors.textColor}
        accentColor={brandColors.buttonColor}
        isMobile={isMobile}
      />

      <main
        className="flex-1 w-full"
        style={{ backgroundColor: tebox.colors.bgPrimary }}
      >
        {step === 'confirmation' && orderResult ? (
          <div
            className={`max-w-2xl ${isMobile ? 'p-4' : 'p-6'} mx-auto`}
            style={{ ...surface.page, backgroundColor: 'transparent' }}
          >
            <div className="space-y-4 py-6">
              <h2
                style={{
                  ...surface.heading,
                  fontSize: teboxDesignSystem.typography.h3.fontSize,
                }}
              >
                Thank you for your purchase
              </h2>
              <p style={surface.muted}>
                Your order total was {formatMoneyFromCents(orderResult.quote.totalCents)}.
              </p>
              {orderResult.giftCards.length > 0 ? (
                <div className="rounded-[6px] p-4 space-y-2" style={shopCardStyle(surface, brandColors)}>
                  <div className="flex items-center gap-2">
                    <GiftCardIcon size={20} color={brandColors.textColor} />
                    <strong style={surface.body}>Gift cards</strong>
                  </div>
                  {orderResult.giftCards.map((gc, index) => {
                    const lineDetail = orderGiftLineDetails[index];
                    const isGift = lineDetail ? isShopGiftCardGiftLine(lineDetail) : false;
                    return (
                      <div key={gc.giftCardId}>
                        <p style={{ ...surface.body, margin: 0 }}>
                          {formatMoneyFromCents(gc.amountCents)}
                          {!isGift && gc.code ? ` — code ${gc.code}` : ''}
                        </p>
                        {isGift && lineDetail?.recipientEmail ? (
                          <p style={{ ...surface.muted, margin: '4px 0 0 0' }}>
                            Gift delivery to {lineDetail.recipientName?.trim() || lineDetail.recipientEmail}
                            {lineDetail.recipientName?.trim() ? ` (${lineDetail.recipientEmail})` : ''}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                  {orderGiftLineDetails.some(isShopGiftCardGiftLine) ? (
                    <p style={{ ...surface.muted, margin: 0 }}>
                      Gift cards sent to a recipient are delivered by email. Cards for your account appear
                      under Account → My gift cards.
                    </p>
                  ) : (
                    <p style={{ ...surface.muted, margin: 0 }}>
                      View your cards under Account → My gift cards.
                    </p>
                  )}
                </div>
              ) : null}
              {orderResult.passes.length > 0 ? (
                <div className="rounded-[6px] p-4 space-y-2" style={shopCardStyle(surface, brandColors)}>
                  <div className="flex items-center gap-2">
                    <PassesIcon size={20} color={brandColors.textColor} />
                    <strong style={surface.body}>Passes</strong>
                  </div>
                  <p style={{ ...surface.body, margin: 0 }}>
                    {orderResult.passes.length} pass(es) added to your account.
                  </p>
                  <p style={{ ...surface.muted, margin: 0 }}>
                    View them under Account → My passes.
                  </p>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setStep('browse');
                  setOrderResult(null);
                  setOrderGiftLineDetails([]);
                  setCheckoutIntent(null);
                }}
                style={{
                  ...surface.primaryButton,
                  padding: '12px 20px',
                }}
              >
                Continue shopping
              </button>
            </div>
          </div>
        ) : (
          <div
            className="flex w-full justify-center"
            style={{
              marginTop: isMobile ? 16 : 32,
              marginBottom: isMobile ? 24 : 32,
              paddingLeft: isMobile ? 16 : 32,
              paddingRight: isMobile ? 16 : 32,
              boxSizing: 'border-box',
            }}
          >
            <div
              className={`flex w-full max-w-[1600px] ${isMobile ? 'flex-col gap-4' : 'flex-row items-start gap-4'}`}
              style={{ boxSizing: 'border-box' }}
            >
              <div
                className={`${isMobile ? 'w-full' : 'flex-1'} min-w-0 bg-white rounded-[6px] overflow-hidden`}
                style={
                  isMobile
                    ? { overflow: 'visible', boxSizing: 'border-box' }
                    : {
                        maxHeight: 'calc(100vh - 160px)',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        boxSizing: 'border-box',
                      }
                }
              >
                <div
                  style={{
                    padding: isMobile ? 16 : 24,
                    boxSizing: 'border-box',
                  }}
                  className={isMobile ? 'space-y-4' : 'space-y-6'}
                >
                {catalogLoading ? (
                  <p style={surface.muted}>Loading shop…</p>
                ) : null}
                {catalogError ? (
                  <p style={{ ...surface.muted, color: '#b91c1c' }}>
                    {catalogError instanceof Error ? catalogError.message : 'Failed to load shop'}
                  </p>
                ) : null}

                {giftConfig?.enabled ? (
                  <ShopGiftCardsSection
                    presetAmountsCents={giftConfig.presetAmountsCents}
                    minCents={giftConfig.minAmountCents}
                    maxCents={giftConfig.maxAmountCents}
                    customValue={customGiftDollars}
                    onCustomChange={setCustomGiftDollars}
                    onAddPreset={openGiftCardModal}
                    onAddCustom={openCustomGiftCardModal}
                    brandColors={brandColors}
                    surface={surface}
                  />
                ) : null}

                {(catalog?.passes?.length ?? 0) > 0 ? (
                  <ShopPassesSection
                    passes={catalog!.passes}
                    cartPassIds={cartPassIds}
                    brandColors={brandColors}
                    surface={surface}
                    onAddPass={addPassToCart}
                  />
                ) : null}

                {(catalog?.memberships?.length ?? 0) > 0 ? (
                  <ShopMembershipsSection
                    memberships={catalog!.memberships}
                    brandColors={brandColors}
                    surface={surface}
                    onRequestMembership={setMembershipRequestTarget}
                  />
                ) : null}

                {membershipRequestSuccess ? (
                  <div
                    className="rounded-[6px] p-4"
                    style={shopCardStyle(surface, brandColors)}
                  >
                    <p style={{ ...surface.body, margin: 0 }}>
                      Your request for {membershipRequestSuccess} was submitted. The club will be in touch.
                    </p>
                  </div>
                ) : null}

                {!catalogLoading &&
                !giftConfig?.enabled &&
                !(catalog?.passes?.length ?? 0) &&
                !(catalog?.memberships?.length ?? 0) ? (
                  <p style={surface.muted}>Nothing is available in the shop yet.</p>
                ) : null}
                </div>
              </div>

              <ShopCheckoutPanel
                isMobile={isMobile}
                brandColors={brandColors}
                surface={surface}
                clubName={clubName}
                paymentsReady={catalog?.paymentsReady ?? false}
                devPaymentBypassAvailable={catalog?.devPaymentBypassAvailable ?? false}
                paymentMethods={paymentMethods}
                selectedPaymentMethodCode={selectedPaymentMethodCode}
                onSelectPaymentMethod={setSelectedPaymentMethodCode}
                walletBackedPaymentMethodsAllowed={walletBackedPaymentMethodsAllowed}
                walletBalanceLabel={walletBalanceLabel}
                walletLoading={walletLoading}
                cartHasGiftCardPurchase={cartHasGiftCardPurchase}
                giftCardCode={giftCardCode}
                onGiftCardCodeChange={setGiftCardCode}
                giftCardValidation={giftCardValidation}
                giftCardValidating={giftCardValidating}
                onValidateGiftCard={() => void validateGiftCard()}
                secondaryPaymentMethodCode={secondaryPaymentMethodCode}
                onSelectSecondaryPaymentMethod={setSecondaryPaymentMethodCode}
                cart={cart}
                quote={quote}
                quoteLoading={quoteLoading}
                authenticatedCustomer={authenticatedCustomer}
                checkoutIntent={checkoutIntent}
                checkoutChargeCents={checkoutChargeCents}
                checkoutError={checkoutError}
                busy={busy}
                onRemoveLine={removeCartLine}
                onSignIn={() => setShowLoginModal(true)}
                onContinueToCardPayment={() => void continueToCardPayment()}
                onPayWithWallet={() => void payWithWallet()}
                onPayWithGiftCard={() => void payWithGiftCard()}
                onPaymentSuccess={({ paymentIntentId }) => void onPaymentSuccess({ paymentIntentId })}
                onPaymentError={(message) => setCheckoutError(message)}
                passLabel={(passProductId) =>
                  catalog?.passes.find((p) => p.id === passProductId)?.name
                }
              />
            </div>
          </div>
        )}
      </main>

      <LoginRegisterModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={(customer) => {
          setAuthenticatedCustomer(customer);
          setShowLoginModal(false);
        }}
        brandColors={brandColors}
        {...(() => {
          const d = decodeClubRouteKey(clubRouteKey);
          return {
            ...(d.bookingSlug ? { bookingSlug: d.bookingSlug } : {}),
            ...(d.clubId ? { clubId: d.clubId } : {}),
            ...(d.clubNumber ? { clubNumber: d.clubNumber } : {}),
          };
        })()}
      />

      <ShopGiftCardPurchaseModal
        open={giftCardModalAmountCents != null}
        amountCents={giftCardModalAmountCents}
        minCents={giftConfig?.minAmountCents}
        maxCents={giftConfig?.maxAmountCents}
        purchaser={authenticatedCustomer}
        brandColors={brandColors}
        onClose={() => setGiftCardModalAmountCents(null)}
        onAddToCart={addGiftCardToCart}
      />

      <ShopMembershipRequestModal
        open={Boolean(membershipRequestTarget)}
        membership={membershipRequestTarget}
        clubRouteKey={clubRouteKey}
        brandColors={brandColors}
        onClose={() => setMembershipRequestTarget(null)}
        onSuccess={(membershipName) => {
          setMembershipRequestSuccess(membershipName);
          setMembershipRequestTarget(null);
        }}
      />
    </div>
  );
};

export default ShopPage;
